// src/services/spkService.js
import api from '../config/axios';

const BASE_PATH = '/spk';

// ============= Query Operations =============

/**
 * Get all active SPKs
 */
export const getAllSPKs = async () => {
  const response = await api.get(BASE_PATH);
  return response.data;
};

/**
 * Get all active SPKs with pagination
 */
export const getAllSPKsPaged = async (page = 0, size = 10, sort = 'spkDate,desc') => {
  const response = await api.get(`${BASE_PATH}/paged`, {
    params: { page, size, sort }
  });
  return response.data;
};

/**
 * Get SPK by ID
 */
export const getSPKById = async (id) => {
  const response = await api.get(`${BASE_PATH}/${id}`);
  return response.data;
};

/**
 * Get SPK by SPK number
 */
export const getSPKByNumber = async (spkNumber) => {
  const response = await api.get(`${BASE_PATH}/number/${spkNumber}`);
  return response.data;
};

/**
 * Search SPKs
 */
export const searchSPKs = async (query, page = 0, size = 10) => {
  const response = await api.get(`${BASE_PATH}/search`, {
    params: { q: query, page, size }
  });
  return response.data;
};

/**
 * Get SPKs by status
 */
export const getSPKsByStatus = async (status, page = 0, size = 10) => {
  const response = await api.get(`${BASE_PATH}/status/${status}`, {
    params: { page, size }
  });
  return response.data;
};

/**
 * Get SPKs by project type
 */
export const getSPKsByProjectType = async (projectType, page = 0, size = 10) => {
  const response = await api.get(`${BASE_PATH}/type/${projectType}`, {
    params: { page, size }
  });
  return response.data;
};

// ============= Command Operations =============

/**
 * Create new SPK
 */
export const createSPK = async (spkData) => {
  const response = await api.post(BASE_PATH, spkData);
  return response.data;
};

/**
 * Update existing SPK
 */
export const updateSPK = async (id, spkData) => {
  const response = await api.put(`${BASE_PATH}/${id}`, spkData);
  return response.data;
};

/**
 * Soft delete SPK
 */
export const softDeleteSPK = async (id) => {
  await api.delete(`${BASE_PATH}/${id}`);
};

// ============= Document Management Operations =============

/**
 * Upload document to SPK
 */
export const uploadDocument = async (spkId, file, description = '') => {
  const formData = new FormData();
  formData.append('file', file);
  if (description) {
    formData.append('description', description);
  }
  
  const response = await api.post(`${BASE_PATH}/${spkId}/documents`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  });
  return response.data;
};

/**
 * Get all documents for an SPK
 */
export const getSPKDocuments = async (spkId) => {
  const response = await api.get(`${BASE_PATH}/${spkId}/documents`);
  return response.data;
};

/**
 * Download document
 */
export const downloadDocument = async (spkId, documentId) => {
  const response = await api.get(
    `${BASE_PATH}/${spkId}/documents/${documentId}/download`,
    { responseType: 'blob' }
  );
  return response.data;
};

/**
 * Delete document from SPK
 */
export const deleteDocument = async (spkId, documentId) => {
  await api.delete(`${BASE_PATH}/${spkId}/documents/${documentId}`);
};

// ============= Helper Functions =============

/**
 * SPK Status Enum (match backend)
 */
export const SPKStatus = {
  ACCEPTED: 'ACCEPTED',
  CONVERTED: 'CONVERTED',
  DELIVERED: 'DELIVERED'
};

/**
 * Get status display name
 */
export const getStatusDisplayName = (status) => {
  const statusMap = {
    ACCEPTED: 'Accepted',
    CONVERTED: 'Converted to Project',
    DELIVERED: 'Delivered'
  };
  return statusMap[status] || status;
};

/**
 * Get status badge color class
 */
export const getStatusBadgeClass = (status) => {
  const colorMap = {
    ACCEPTED: 'bg-blue-100 text-blue-800',
    CONVERTED: 'bg-green-100 text-green-800',
    DELIVERED: 'bg-purple-100 text-purple-800'
  };
  return colorMap[status] || 'bg-gray-100 text-gray-800';
};

/**
 * Project Type Enum (match backend)
 */
export const ProjectType = {
  TOWER: 'TOWER',
  FO: 'FO',
  TOWER_COLO: 'TOWER_COLO'
};

/**
 * Download document helper (trigger browser download)
 */
export const triggerDocumentDownload = async (spkId, documentId, fileName) => {
  try {
    const blob = await downloadDocument(spkId, documentId);
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  } catch (error) {
    console.error('Error downloading document:', error);
    throw error;
  }
};

export default {
  getAllSPKs,
  getAllSPKsPaged,
  getSPKById,
  getSPKByNumber,
  searchSPKs,
  getSPKsByStatus,
  getSPKsByProjectType,
  createSPK,
  updateSPK,
  softDeleteSPK,
  uploadDocument,
  getSPKDocuments,
  downloadDocument,
  deleteDocument,
  SPKStatus,
  getStatusDisplayName,
  getStatusBadgeClass,
  ProjectType,
  triggerDocumentDownload
};